import {useMemo, useState, type ReactNode} from 'react';
import styled from 'styled-components';
import {EmptyState} from '../../tickets/components/EmptyState';
import {DashboardGroupByControl} from './DashboardGroupByControl';
import {DashboardGroupHeader} from './DashboardGroupHeader';
import {useDashboardGroupCollapse} from '../hooks/useDashboardGroupCollapse';
import {groupTickets, type DashboardGroupByToken, type TicketGroup} from '../utils/groupTickets';
import type {GroupableTicket} from '../types/groupableTicket';

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[400]};
	margin-top: ${p => p.theme.sys.spacing[600]};
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[400]};
`;

const Groups = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[600]};
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;

interface Props<T extends GroupableTicket> {
	tickets: readonly T[];
	/**
	 * Renders one group's rows. Optional because grouping is a layout over whatever
	 * row presentation the dashboard has — the dashboard's ticket table is FOR-73's,
	 * and its data is FOR-70's, so until those land there is nothing to put inside a
	 * group and this section correctly renders its empty state instead. Wiring a
	 * placeholder row renderer here would ship UI nobody asked for and then have to
	 * be unpicked.
	 */
	renderGroup?: (tickets: T[], group: TicketGroup<T>) => ReactNode;
}

/**
 * The dashboard's grouped ticket section: the group-by control plus, under any
 * token other than "No grouping", one expandable header per bucket carrying that
 * bucket's subtotals (PRD §6.7).
 *
 * Grouping is client-side only — the token re-lays-out the rows already loaded and
 * issues no query, which is the PRD §13 acceptance criterion. That is why the
 * token is local state here rather than a URL param feeding a refetch.
 *
 * Under "No grouping" the rows render flat, with no headers: `groupTickets`
 * returns a single all-tickets bucket for that token, so the flat and grouped
 * paths share one code path and can't drift.
 *
 * The control lives here rather than in the page toolbar because the real
 * dashboard toolbar is FOR-71's; this keeps the route diff small while three
 * sibling PRs are in flight over the same file. Move it into the toolbar when
 * that lands.
 */
export function DashboardGroupedTickets<T extends GroupableTicket>({tickets, renderGroup}: Props<T>) {
	const [groupBy, setGroupBy] = useState<DashboardGroupByToken>('none');
	const {isCollapsed, toggle} = useDashboardGroupCollapse(groupBy);

	const groups = useMemo(() => groupTickets(tickets, groupBy), [tickets, groupBy]);

	return (
		<Section data-testid="dashboard-grouped-tickets">
			<Toolbar>
				<DashboardGroupByControl value={groupBy} onChange={setGroupBy} />
			</Toolbar>

			{groups.length === 0 ? (
				<EmptyState variant="inset" data-testid="dashboard-no-tickets">
					No tickets in this period.
				</EmptyState>
			) : (
				<Groups>
					{groups.map(group => {
						// Under "No grouping" the single bucket renders its rows with no
						// header — the bucket exists to keep one code path, not to be shown.
						if (groupBy === 'none') {
							return <Group key={group.key}>{renderGroup?.(group.tickets, group)}</Group>;
						}
						const collapsed = isCollapsed(group.key);
						const contentId = `dashboard-group-content-${group.domKey}`;
						return (
							<Group key={group.key} data-testid={`dashboard-group-${group.domKey}`}>
								<DashboardGroupHeader
									domKey={group.domKey}
									label={group.label}
									stats={group.stats}
									collapsed={collapsed}
									contentId={contentId}
									onToggle={() => toggle(group.key)}
								/>
								<div id={contentId} data-testid={contentId} hidden={collapsed}>
									{renderGroup?.(group.tickets, group)}
								</div>
							</Group>
						);
					})}
				</Groups>
			)}
		</Section>
	);
}
